<template>
  <button 
    @click="scrollTop(400)"
    class="scroll-to-top-button"
    :class="{ 'visible': visible }"
    aria-label="Grįžti į viršų"
  >
    <div class="scroll-icon-wrapper">
      <ArrowUpIcon class="scroll-icon"></ArrowUpIcon>
    </div>
    <div class="scroll-pulse"></div>
    <div class="scroll-ripple"></div>
  </button>
</template>

<script>
import { ArrowUpIcon } from '@heroicons/vue/outline'
import { ref, onMounted, onUnmounted } from 'vue';

export default {
  name: "ScrollToTop",
  components: {
    ArrowUpIcon,
  },
  setup() {
    const visible = ref(false);
    const scrollThreshold = 300; // Show button after scrolling 300px

    const handleScroll = () => {
      visible.value = window.scrollY > scrollThreshold;
    };

    const scrollTop = (scrollDuration) => {
      const scrollStep = -window.scrollY / (scrollDuration / 15);
      const scrollInterval = setInterval(function() {
        if (window.scrollY != 0) {
          window.scrollBy(0, scrollStep);
        } else {
          clearInterval(scrollInterval);
        }
      }, 15);
    };

    onMounted(() => {
      window.addEventListener('scroll', handleScroll);
      handleScroll(); // Check initial scroll position
    });

    onUnmounted(() => {
      window.removeEventListener('scroll', handleScroll);
    });

    return {
      visible,
      scrollTop,
    };
  }
}
</script>

<style scoped>
.scroll-to-top-button {
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, #2c3e50, #34495e);
  color: white;
  border-radius: 50%;
  border: 3px solid rgba(255, 255, 255, 0.4);
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 25px rgba(44, 62, 80, 0.5),
              0 0 0 0 rgba(44, 62, 80, 0.7),
              0 0 0 0 rgba(44, 62, 80, 0.7);
  z-index: 9997;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-family: 'Montserrat', sans-serif;
  cursor: pointer;
  backdrop-filter: blur(10px);
  overflow: visible;
  opacity: 0;
  visibility: hidden;
  transform: translateY(20px) scale(0.8);
}

.scroll-to-top-button.visible {
  opacity: 1;
  visibility: visible;
  transform: translateY(0) scale(1);
  animation: slideInUp 0.5s ease, pulseRing 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.scroll-to-top-button:hover {
  transform: translateY(-8px) scale(1.1);
  box-shadow: 0 15px 40px rgba(44, 62, 80, 0.6),
              0 0 0 8px rgba(44, 62, 80, 0.1),
              0 0 0 16px rgba(44, 62, 80, 0.05);
  border-color: rgba(255, 255, 255, 0.7);
  background: linear-gradient(135deg, #34495e, #2c3e50);
  color: white;
}

.scroll-to-top-button:active {
  transform: translateY(-4px) scale(1.05);
  box-shadow: 0 10px 30px rgba(44, 62, 80, 0.5);
}

.scroll-icon-wrapper {
  width: 100%;
  height: 100%;
  background: transparent;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 2;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.scroll-to-top-button:hover .scroll-icon-wrapper {
  transform: translateY(-3px);
}

.scroll-icon {
  width: 32px;
  height: 32px;
  color: white;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
  transition: all 0.3s ease;
}

.scroll-to-top-button:hover .scroll-icon {
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
  transform: translateY(-2px);
}

.scroll-pulse {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: linear-gradient(135deg, #2c3e50, #34495e);
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  z-index: -1;
  opacity: 0.6;
}

.scroll-ripple {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.3);
  animation: ripple 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  z-index: -2;
}

@keyframes slideInUp {
  from {
    transform: translateY(20px) scale(0.8);
    opacity: 0;
  }
  to {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
}

@keyframes pulse {
  0% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.6;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.2);
    opacity: 0.3;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.4);
    opacity: 0;
  }
}

@keyframes pulseRing {
  0% {
    box-shadow: 0 8px 25px rgba(44, 62, 80, 0.5),
                0 0 0 0 rgba(44, 62, 80, 0.7),
                0 0 0 0 rgba(44, 62, 80, 0.7);
  }
  50% {
    box-shadow: 0 8px 25px rgba(44, 62, 80, 0.5),
                0 0 0 8px rgba(44, 62, 80, 0.3),
                0 0 0 16px rgba(44, 62, 80, 0.1);
  }
  100% {
    box-shadow: 0 8px 25px rgba(44, 62, 80, 0.5),
                0 0 0 0 rgba(44, 62, 80, 0),
                0 0 0 0 rgba(44, 62, 80, 0);
  }
}

@keyframes ripple {
  0% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.6;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.8);
    opacity: 0;
  }
}

@media (max-width: 768px) {
  .scroll-to-top-button {
    bottom: 20px;
    right: 20px;
    width: 60px;
    height: 60px;
    border-width: 2px;
  }
  
  .scroll-icon {
    width: 28px;
    height: 28px;
  }
}
</style>